import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import {
    mdiSchool,
    mdiAccountMultiplePlusOutline,
    mdiAccountPlusOutline,
    mdiTrashCan,
    mdiAccountGroup,
    mdiCreditCardCheckOutline,
    mdiCreditCardClockOutline,
    mdiCheckCircleOutline,
} from '@mdi/js';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import { classApi } from '../../api/classes';
import ConfirmationModal from '../../components/ConfirmationModal';
import EnrolStudentModal from '../../components/classes/EnrolStudentModal';
import AddStudentModal from '../../components/classes/AddStudentModal';
import AdminPaymentModal from '../../components/classes/AdminPaymentModal';
import ChangeRoleModal from '../../components/classes/ChangeRoleModal';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './ShortCourseDetailPage.css';
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ShortCourseDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const orgId = user?.orgId || '';

    const [course, setCourse] = useState(null);
    const [enrolments, setEnrolments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showEnrolModal, setShowEnrolModal] = useState(false);
    const [showWalkInModal, setShowWalkInModal] = useState(false);
    const [paymentStudent, setPaymentStudent] = useState(null);
    const [changeRoleEnrolment, setChangeRoleEnrolment] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [enrolmentTab, setEnrolmentTab] = useState('enrolled'); // 'enrolled' | 'waitlisted'
    const [approvingId, setApprovingId] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    const loadCourse = useCallback(async () => {
        const res = await classApi.getShortCourse(id);
        if (res.ok) setCourse(res.data.shortCourse);
    }, [id]);

    const loadEnrolments = useCallback(async () => {
        const res = await classApi.getShortCourseEnrolments(id);
        if (res.ok) setEnrolments(res.data.enrolments || []);
    }, [id]);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([loadCourse(), loadEnrolments()]).finally(() => setIsLoading(false));
    }, [loadCourse, loadEnrolments]);

    const handleToggleRestriction = async (field) => {
        if (!course) return;
        const current = course.enrolmentRestriction || {};
        const newRestrict = {
            restrictLeaders: current.restrictLeaders === true,
            restrictFollowers: current.restrictFollowers === true,
            [field]: !current[field],
        };
        try {
            const res = await classApi.updateShortCourseRestriction(id, newRestrict.restrictLeaders, newRestrict.restrictFollowers);
            if (res.ok) {
                setCourse(res.data.shortCourse);
                toast.success('Enrolment pause updated');
            } else {
                toast.error(res.data?.message || 'Failed to update restriction');
            }
        } catch {
            toast.error('Failed to update restriction');
        }
    };

    const handleApproveWaitlisted = async (enrolmentId, studentName) => {
        setApprovingId(enrolmentId);
        try {
            const res = await classApi.updateEnrolmentStatus(enrolmentId, 'confirmed');
            if (res.ok) {
                toast.success(`${studentName} approved and enrolled`);
                loadEnrolments();
            } else {
                toast.error(res.data?.message || 'Failed to approve enrolment');
            }
        } catch {
            toast.error('Failed to approve enrolment');
        } finally {
            setApprovingId(null);
        }
    };

    const handleRemove = (enrolmentId, studentName) => {
        setConfirmModal({
            title: 'Remove Student',
            message: `Remove ${studentName} from this course?`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                setRemovingId(enrolmentId);
                try {
                    const res = await classApi.removeEnrolment(enrolmentId, 0, false);
                    if (res.ok) {
                        toast.success(`${studentName} removed`);
                        loadEnrolments();
                    } else {
                        toast.error(res.data?.message || 'Failed to remove enrolment');
                    }
                } catch {
                    toast.error('Failed to remove enrolment');
                } finally {
                    setRemovingId(null);
                }
            },
        });
    };

    const formatDate = iso =>
        iso
            ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
            : '—';

    const roleLabel = r => (r === 'leader' ? 'Leader' : r === 'follower' ? 'Follower' : '—');

    return (
        <div className="short-course-detail-page dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button
                                className="back-nav-btn"
                                onClick={() => navigate('/classes/terms')}>
                                ← Terms &amp; Courses
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon path={mdiSchool} size={1.1} color="#451f78" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                {isLoading ? 'Short Course' : (course?.name || 'Short Course')}
                            </h1>
                        </div>
                        {!isLoading && course && (
                            <p className="sub-portal-header-subtitle">
                                {[
                                    course.style,
                                    course.level,
                                    course.dayOfWeek != null ? DAY_NAMES[course.dayOfWeek] : null,
                                    course.startTime ? `${course.startTime}–${course.endTime}` : null,
                                    course.startDate ? `From ${formatDate(course.startDate)}` : null,
                                    course.sessionCount ? `${course.sessionCount} sessions` : null,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </p>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="content-card">
                            <p className="sc-detail-loading">Loading...</p>
                        </div>
                    ) : (
                        <div className="content-card">
                            <div className="sc-detail-enrolments-header">
                                <div className="sc-detail-enrolments-title">
                                    <Icon path={mdiAccountGroup} size={0.95} color="#451f78" />
                                    <h3>
                                        Enrolments ({enrolments.filter(e => e.status !== 'pending').length}
                                        {course?.maxStudents ? `/${course.maxStudents}` : ''})
                                    </h3>
                                </div>
                                <div className="sc-detail-enrolments-actions">
                                    <button className="btn-purple" onClick={() => setShowEnrolModal(true)}>
                                        <Icon path={mdiAccountMultiplePlusOutline} size={0.85} />
                                        Enrol Student
                                    </button>
                                    <button className="btn-turquoise" onClick={() => setShowWalkInModal(true)}>
                                        <Icon path={mdiAccountPlusOutline} size={0.85} />
                                        Add Walk-in
                                    </button>
                                </div>
                            </div>

                            {/* Pause enrolment toggles */}
                            <div className="sc-detail-restriction-row">
                                <div className="sc-detail-restriction-top">
                                    <span className="sc-detail-restriction-label">Pause enrolment:</span>
                                    <button
                                        className={`sc-detail-restrict-btn${course?.enrolmentRestriction?.restrictLeaders ? ' active' : ''}`}
                                        onClick={() => handleToggleRestriction('restrictLeaders')}
                                        title="When active, new leader enrolments go to waitlist">
                                        Leaders {course?.enrolmentRestriction?.restrictLeaders ? '(paused)' : ''}
                                    </button>
                                    <button
                                        className={`sc-detail-restrict-btn${course?.enrolmentRestriction?.restrictFollowers ? ' active' : ''}`}
                                        onClick={() => handleToggleRestriction('restrictFollowers')}
                                        title="When active, new follower enrolments go to waitlist">
                                        Followers {course?.enrolmentRestriction?.restrictFollowers ? '(paused)' : ''}
                                    </button>
                                </div>
                                <span className="sc-detail-restriction-hint">
                                    Paused roles will be waitlisted instead of enrolled directly
                                </span>
                            </div>

                            {/* Enrolled / Waitlisted tabs */}
                            {enrolments.some(e => e.status === 'pending') && (
                                <div className="sc-detail-tabs">
                                    <button
                                        className={`sc-detail-tab${enrolmentTab === 'enrolled' ? ' active' : ''}`}
                                        onClick={() => setEnrolmentTab('enrolled')}>
                                        Enrolled ({enrolments.filter(e => e.status !== 'pending').length})
                                    </button>
                                    <button
                                        className={`sc-detail-tab${enrolmentTab === 'waitlisted' ? ' active' : ''}`}
                                        onClick={() => setEnrolmentTab('waitlisted')}>
                                        Waitlisted ({enrolments.filter(e => e.status === 'pending').length})
                                    </button>
                                </div>
                            )}

                            {enrolments.length === 0 ? (
                                <p className="sc-detail-empty">No students enrolled yet.</p>
                            ) : (
                                <div className="sc-detail-enrolment-list">
                                    {enrolments
                                        .filter(e => {
                                            const hasPending = enrolments.some(x => x.status === 'pending');
                                            if (!hasPending) return e.status !== 'pending';
                                            return enrolmentTab === 'waitlisted' ? e.status === 'pending' : e.status !== 'pending';
                                        })
                                        .map(e => {
                                        const firstName = e.student?.firstName || '';
                                        const lastName = e.student?.lastName || '';
                                        const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
                                        const fullName = `${firstName} ${lastName}`.trim();
                                        const isPaid = e.paymentStatus === 'paid';
                                        const isWaitlisted = e.status === 'pending';
                                        return (
                                        <div key={e.id} className="sc-detail-student-card">
                                            <div className="sc-detail-student-avatar">
                                                <span className="sc-detail-student-initials">{initials}</span>
                                            </div>
                                            <div className="sc-detail-student-info">
                                                <div className="sc-detail-student-name">{fullName}</div>
                                            </div>
                                            <div className="sc-detail-card-status">
                                                {!isWaitlisted && (
                                                    <span className={`sc-detail-status-pill ${
                                                        e.status === 'confirmed' ? 'confirmed'
                                                        : e.status === 'unconfirmed' ? 'unconfirmed'
                                                        : e.status === 'inactive' ? 'inactive'
                                                        : 'pending'
                                                    }`}>
                                                        {e.status === 'confirmed' ? 'Enrolled'
                                                        : e.status === 'unconfirmed' ? 'Unconfirmed'
                                                        : e.status === 'inactive' ? 'Inactive'
                                                        : e.status}
                                                    </span>
                                                )}
                                                {e.role && (
                                                    <button
                                                        className={`sc-detail-role-pill ${e.role}`}
                                                        onClick={(ev) => { ev.stopPropagation(); setChangeRoleEnrolment(e); }}
                                                        title={`Change role (currently ${e.role})`}>
                                                        {e.role === 'leader' ? 'L' : 'F'}
                                                    </button>
                                                )}
                                            </div>
                                            {isWaitlisted ? (
                                                <button
                                                    className="sc-detail-action-btn approve"
                                                    title="Approve and enrol"
                                                    onClick={() => handleApproveWaitlisted(e.id, fullName)}
                                                    disabled={approvingId === e.id}>
                                                    <Icon path={mdiCheckCircleOutline} size={0.9} color="var(--dl-color-greentick)" />
                                                </button>
                                            ) : (
                                                <button
                                                    className={`sc-detail-action-btn payment${isPaid ? ' paid' : ''}`}
                                                    title={isPaid ? 'Paid' : 'Payment outstanding'}
                                                    onClick={() =>
                                                        setPaymentStudent({
                                                            id: e.studentProfileId,
                                                            name: fullName,
                                                        })
                                                    }>
                                                    <Icon
                                                        path={isPaid ? mdiCreditCardCheckOutline : mdiCreditCardClockOutline}
                                                        size={1.1}
                                                        color={isPaid ? 'var(--dl-color-gray-white)' : 'var(--dl-color-needs-action)'}
                                                    />
                                                </button>
                                            )}
                                            <button
                                                className="sc-detail-action-btn danger"
                                                title="Remove enrolment"
                                                onClick={() => handleRemove(e.id, fullName)}
                                                disabled={removingId === e.id}>
                                                <Icon path={mdiTrashCan} size={0.8} />
                                            </button>
                                        </div>
                                        );
                                    })}
                                    {enrolments.filter(e => enrolmentTab === 'waitlisted' ? e.status === 'pending' : e.status !== 'pending').length === 0 && (
                                        <p className="sc-detail-empty">No students in this list.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showEnrolModal && (
                <EnrolStudentModal
                    shortCourseId={id}
                    className={course?.name}
                    onClose={() => setShowEnrolModal(false)}
                    onEnrolled={loadEnrolments}
                />
            )}
            {showWalkInModal && (
                <AddStudentModal
                    shortCourseId={id}
                    onClose={() => setShowWalkInModal(false)}
                    onStudentAdded={loadEnrolments}
                />
            )}
            {paymentStudent && (
                <AdminPaymentModal
                    isOpen={!!paymentStudent}
                    onClose={() => setPaymentStudent(null)}
                    studentProfileId={paymentStudent.id}
                    orgId={orgId}
                    studentName={paymentStudent.name}
                />
            )}
            {changeRoleEnrolment && (
                <ChangeRoleModal
                    enrolment={changeRoleEnrolment}
                    onClose={() => setChangeRoleEnrolment(null)}
                    onRoleChanged={() => {
                        setChangeRoleEnrolment(null);
                        loadEnrolments();
                    }}
                />
            )}

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default ShortCourseDetailPage;
